import {
  FormInput,
  PaymentProcessorEnum,
  PrimaryButton,
  getPaymentProcessorTitle,
} from '@systemeio/ui'
import HelpCenterLink from 'modules/profile/payment-gateways/components/help-center-link'
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations'
import { getConnectionWebhookBaseUrl } from 'modules/profile/payment-gateways/integrations/secure-and-pay/constants'
import { useSecureAndPayRestApi } from 'modules/profile/payment-gateways/integrations/secure-and-pay/hooks'
import {
  PaymentSecureAndPayErrorInterface,
  PaymentSecureAndPayInterface,
} from 'modules/profile/payment-gateways/integrations/secure-and-pay/types'
import { getSecureAndPayHelpPageLink } from 'modules/profile/payment-gateways/integrations/secure-and-pay/utils'
import React, { useState } from 'react'
import { CopyToClipboardButton } from 'shared/components/copy-to-clipboard-button'
import CreateModal from 'shared/components/create-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'

const defaultValues: PaymentSecureAndPayInterface = {
  siteId: 0,
  key: '',
}

const defaultErrors: PaymentSecureAndPayErrorInterface = {
  siteId: '',
  key: '',
}

const SecureAndPayCreateModal = () => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const [opened, setOpened] = useState(false)
  const [errors, setErrors] = useState<PaymentSecureAndPayErrorInterface>(defaultErrors)
  const { connect } = useSecureAndPayRestApi({
    badRequestHandler: error => setErrors({ ...defaultErrors, ...error.errors.fields }),
  })

  const { mutatePaymentIntegrations } = useConnectedPaymentIntegrations()

  const connectionWebhookUrl = `${getConnectionWebhookBaseUrl()}/${user?.slug}`

  const onCreate = async (data: PaymentSecureAndPayInterface) => {
    await connect(data)
    await mutatePaymentIntegrations()
  }

  return (
    <>
      <PrimaryButton
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault()
          setOpened(true)
        }}
        className={'font-bold'}
      >
        {t('dashboard.settings.payment_gateways.connect')}
      </PrimaryButton>
      <CreateModal
        fullCaption={t('dashboard.settings.payment_gateways.connect_payment_title', {
          payment: getPaymentProcessorTitle(PaymentProcessorEnum.SecureAndPay),
        })}
        caption={getPaymentProcessorTitle(PaymentProcessorEnum.SecureAndPay)}
        opened={opened}
        onClose={() => setOpened(false)}
        defaultValues={defaultValues}
        needToast={false}
        creatable={[
          {
            field: 'siteId',
            onRender: (value, onChange) => (
              <FormInput
                key={'secure-and-pay-site-id-create'}
                label={t('dashboard.settings.payment-gateways.site_id')}
                value={value}
                type="number"
                onChange={e => onChange(e.target.valueAsNumber)}
                error={errors.siteId}
              />
            ),
          },
          {
            field: 'key',
            onRender: (value, onChange) => (
              <FormInput
                key={'secure-and-pay-key-create'}
                label={t('dashboard.settings.payment-gateways.api_secret_key')}
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.key}
              />
            ),
          },
        ]}
        modalDescription={
          <div className={'flex items-baseline gap-1 truncate text-base lg:text-lg'}>
            <span>{`${t('dashboard.settings.payment-gateways.webhook_url')}:`}</span>
            <span className={'text-text-primary truncate'} title={connectionWebhookUrl}>
              {connectionWebhookUrl}
            </span>
            <CopyToClipboardButton text={connectionWebhookUrl} />
          </div>
        }
        additionalInfo={
          <HelpCenterLink
            link={getSecureAndPayHelpPageLink(user?.dashboardLocale)}
            integration={getPaymentProcessorTitle(PaymentProcessorEnum.SecureAndPay)}
          />
        }
        onCreate={onCreate}
        requiredFields={['siteId', 'key']}
      />
    </>
  )
}

export default SecureAndPayCreateModal
